// پنج قدمِ اصلیِ قیف — WHOSUP-291.
//
// تا پیش از این `app_events` فقط صفحه و لمس داشت؛ از روی آن‌ها نمی‌شد گفت
// کاربر رویدادی را باز کرد، جواب داد، فرستاد یا کسی را دعوت کرد — فقط اینکه
// به کدام آدرس رفت و کدام دکمه را زد. این پنج‌تا رویدادهای **جدا و بانام**‌اند
// و عمداً جای `tap` را نمی‌گیرند: متنِ دکمه با هر ویرایشِ رابط عوض می‌شود،
// نامِ قدمِ قیف نباید عوض شود.
//
// این فایل خالص است (بی‌شبکه، بی‌تایمر) تا با `npm test` آزموده شود؛
// فرستادن کارِ `analytics.ts` است و از همان صفِ همیشگی می‌رود — یعنی هیچ
// قدمی منتظرِ جوابِ دیتابیس نمی‌ماند.
//
// ⚠ هیچ‌کدام توکنِ لینک نمی‌گیرند. توکن خودش کلیدِ ورود است؛ شناسه‌ی سیگنال
// یا رویداد کافی است و چیزی را باز نمی‌کند.

/** نام‌های ثابتِ پنج قدم — به همین ترتیبِ قیف. */
export const FUNNEL_EVENTS = [
  'link_entry',   // ورود از لینکِ دعوت یا لینکِ عمومی
  'event_open',   // باز شدنِ صفحه‌ی یک رویداد/سیگنال
  'rsvp',         // پاسخ یا اعلامِ علاقه
  'share',        // فرستادنِ لینک (کپی یا برگه‌ی اشتراک)
  'invite_sent',  // دعوتِ دوست یا گروه
] as const;

export type FunnelName = typeof FUNNEL_EVENTS[number];

/** سیگنالِ یک کاربر، یا رویدادِ صفحه‌ی کشف. */
export type FunnelSource = 'signal' | 'discover';

/** از کجا به صفحه‌ی رویداد رسید. */
export type OpenOrigin =
  | 'feed' | 'discover' | 'public_link' | 'invite_link' | 'notification' | 'other';

export type RsvpResponse =
  | 'in' | 'out' | 'maybe' | 'conditional' | 'pending' | 'expired' | 'interested' | 'uninterested';

/** شکلِ دقیقِ ویژگی‌های هر قدم. */
export interface FunnelProps {
  link_entry: { kind: 'invite' | 'public'; id?: string };
  /** `id` در لینکِ دعوتِ شخصی نیست: آن دریچه شناسه‌ی سیگنال را نمی‌دهد. */
  event_open: { id?: string; source: FunnelSource; from: OpenOrigin };
  rsvp: { id?: string; source: FunnelSource; response: RsvpResponse };
  share: { id: string; source: FunnelSource; method: 'copy' | 'sheet'; link: 'public' | 'invite' };
  invite_sent: { id: string; target: 'friend' | 'group'; count: number };
}

/* ───────────────────── «از کجا آمد» ───────────────────── */

/**
 * مبدأِ باز شدن، از روی صفحه‌ی قبلی (همان الگوی `normalizeScreen`).
 *
 * `hint` بر صفحه‌ی قبلی مقدم است: ضربه روی اعلانِ پوش از هر صفحه‌ای ممکن
 * است بیاید، پس صفحه‌ی قبلی آن‌جا دروغ می‌گوید.
 */
export function openOrigin(prevScreen: string | null, hint?: OpenOrigin | null): OpenOrigin {
  if (hint) return hint;
  const p = prevScreen ?? '';
  if (p === '/' || p === '/signals' || p === '/home' || p === '/index') return 'feed';
  if (p === '/discover' || p.startsWith('/discover/')) return 'discover';
  if (p === '/notifications') return 'notification';
  if (p.startsWith('/s/')) return 'public_link';
  if (p.startsWith('/i/')) return 'invite_link';
  return 'other';
}

/* ───────────────────── تکرار ───────────────────── */

/** دو بار همان قدم برای همان شناسه در این فاصله = یک بار (StrictMode، رفرشِ کوئری). */
export const FUNNEL_DEDUPE_MS = 2_000;

export const funnelKey = (name: string, props: Record<string, unknown>): string =>
  `${name}|${String(props.id ?? '')}|${String(props.response ?? props.method ?? props.kind ?? props.target ?? '')}`;

export const isRepeatFunnel = (
  last: { key: string; at: number } | null, key: string, at: number,
): boolean => !!last && last.key === key && at - last.at < FUNNEL_DEDUPE_MS;
